/**
 * F2 — CLP line editor rework (v2).
 * Reads getClpLines({constructionLinkedPlanId}) + useWbsStages().
 * Rows carry wbsStageId/floorTarget/percentConsideration.
 * Target input gated by isSlab(...) via byId.
 * Add-row default = first active clpEligible stage's wbsStageId.
 * POST→createClpLine (clpLineId returned), DELETE→deleteClpLine(clpLineId).
 */
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Button } from "@material-tailwind/react";
import { FaPlus } from "react-icons/fa";
import usePrivilege from "../../hooks/usePrivilege";
import { DeleteButton, EditButton, OutlineButton, PrimaryButton, SecondaryButton } from "../Buttons";
import { Edit, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";
import { useWbsStages, isSlab } from "../../constants/wbsStages";
import { getClpLines, createClpLine, deleteClpLine } from "../../services/wbs/index";

function ProjectStatus({ projectId, setProjectStatusType, constructionLinkedPlanId }) {
  const [projectStatus, setProjectStatus] = useState([]);
  const [originalStatus, setOriginalStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const { hasPrivilege } = usePrivilege();
  const [isAddRowDisabled, setIsAddRowDisabled] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [savingRowId, setSavingRowId] = useState(null);

  // v2: load catalog from /wbs/stages (replaces /status/type)
  const { stages, byId, byTypeName, loading: stagesLoading } = useWbsStages();

  // First active clpEligible stage for new-row default
  const defaultStageId = stages.find((s) => s.isActive && s.clpEligible)?.wbsStageId;

  useEffect(() => {
    const fetchData = async () => {
      if (!constructionLinkedPlanId) return;
      try {
        setLoading(true);
        // v2: getClpLines replaces GET /project/status
        const clpLines = await getClpLines({ constructionLinkedPlanId });
        const linesArray = Array.isArray(clpLines) ? clpLines : (clpLines?.items || []);
        setProjectStatus(linesArray);
        setOriginalStatus([...linesArray]);
        // Provide wbsStage descriptors to parent (replaces old statusType array)
        if (setProjectStatusType) {
          setProjectStatusType(linesArray.map((i) => i.wbsStage));
        }
        setEditingRowId(null);
        setIsAddRowDisabled(false);
      } catch (err) {
        console.error("Error fetching CLP lines:", err);
        setError("Failed to fetch CLP lines.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [constructionLinkedPlanId, setProjectStatusType]);

  const handleAddRow = useCallback(() => {
    if (isAddRowDisabled || stagesLoading) return;

    const maxOrder = projectStatus.reduce((max, current) => Math.max(max, current.order), 0);

    const newRow = {
      order: maxOrder + 1,
      particulars: "",
      percentConsideration: 0,
      // v2: default = first active clpEligible stage's wbsStageId (NOT a hardcoded id)
      wbsStageId: defaultStageId,
      floorTarget: null,
      isNew: true,
    };

    setProjectStatus((prevStatus) => [...prevStatus, newRow].sort((a, b) => a.order - b.order));
    setEditingRowId(maxOrder + 1);
    setIsAddRowDisabled(true);
  }, [projectStatus, defaultStageId, isAddRowDisabled, stagesLoading]);

  const handleCancelNewRowEdit = useCallback((order) => {
    setProjectStatus((prevStatus) => {
      const rowToCancel = prevStatus.find(row => row.order === order);
      if (rowToCancel && rowToCancel.isNew) {
        return prevStatus.filter((row) => row.order !== order);
      }
      const originalRow = originalStatus.find(row => row.order === order);
      if (originalRow) {
        return prevStatus.map(row =>
          row.order === order ? { ...originalRow } : row
        );
      }
      return prevStatus;
    });
    setEditingRowId(null);
    setIsAddRowDisabled(false);
  }, [originalStatus]);

  const handleEditRow = useCallback((order) => {
    setEditingRowId(order);
    setIsAddRowDisabled(true);
  }, []);

  const handleSaveRow = useCallback(async (row) => {
    setSavingRowId(row.order);
    try {
      // v2: resolve wbsStageId from wbsStage embed or direct field
      const wbsStageId = row.wbsStage?.wbsStageId || row.wbsStageId;
      const data = {
        constructionLinkedPlanId: constructionLinkedPlanId,
        order: row.order,
        particulars: row.particulars,
        percentConsideration: parseInt(row.percentConsideration, 10),
        wbsStageId: wbsStageId,
        floorTarget: row.floorTarget ? parseInt(row.floorTarget, 10) : null,
      };

      if (row.isNew) {
        // v2: POST→createClpLine (returns clpLineId)
        await createClpLine(data);
      } else {
        // PUT with clpLineId (v2 surrogate key)
        await createClpLine({ ...data, clpLineId: row.clpLineId });
      }

      // Refresh
      const refreshed = await getClpLines({ constructionLinkedPlanId });
      const refreshedArray = Array.isArray(refreshed) ? refreshed : (refreshed?.items || []);
      setProjectStatus(refreshedArray);
      setOriginalStatus([...refreshedArray]);
      if (setProjectStatusType) {
        setProjectStatusType(refreshedArray.map(i => i.wbsStage));
      }
      toast.success(`CLP line ${row.isNew ? "created" : "updated"} successfully!`);
    } catch (err) {
      console.error("Error saving CLP line:", err);
      setError("Failed to save CLP line.");
      toast.error("Failed to save CLP line.");
    } finally {
      setEditingRowId(null);
      setIsAddRowDisabled(false);
      setSavingRowId(null);
    }
  }, [constructionLinkedPlanId, setProjectStatusType]);

  const confirmDeleteRow = (row) => {
    setRowToDelete(row);
    setShowConfirmationModal(true);
  };

  const handleDeleteRow = useCallback(async () => {
    try {
      // v2: DELETE by clpLineId (NOT composite key)
      await deleteClpLine(rowToDelete.clpLineId);

      const updatedStatus = projectStatus.filter(row => row.clpLineId !== rowToDelete.clpLineId);
      const originalStatusAfterDelete = originalStatus.filter(row => row.clpLineId !== rowToDelete.clpLineId);
      setProjectStatus(updatedStatus);
      setOriginalStatus(originalStatusAfterDelete);
      toast.success("CLP line deleted successfully!");
    } catch (err) {
      console.error("Error deleting CLP line:", err);
      setError("Failed to delete CLP line.");
      toast.error("Failed to delete CLP line.");
    } finally {
      setShowConfirmationModal(false);
      setRowToDelete(null);
    }
  }, [projectStatus, rowToDelete, originalStatus]);

  const handleCancelDelete = () => {
    setShowConfirmationModal(false);
    setRowToDelete(null);
  };

  const handleInputChange = useCallback((event, order, field) => {
    event.stopPropagation();
    setProjectStatus(prevStatus =>
      prevStatus.map(row => (row.order === order ? { ...row, [field]: event.target.value } : row))
    );
  }, []);

  const handleSelectChange = useCallback((event, order) => {
    event.stopPropagation();
    // v2: resolve by wbsStageId from catalog (not typeName comparison)
    const selectedStageId = parseInt(event.target.value, 10);
    const selectedStage = byId[selectedStageId];
    setProjectStatus(prevStatus =>
      prevStatus.map(row =>
        row.order === order
          ? {
            ...row,
            wbsStage: selectedStage,
            wbsStageId: selectedStageId,
            floorTarget: null,
          }
          : row
      )
    );
  }, [byId]);

  const renderStatusType = (row) => {
    if (stagesLoading) return "Loading...";
    const isEditingRow = row.order === editingRowId;
    // v2: resolve from wbsStage embed or byId lookup
    const currentStageId = row.wbsStage?.wbsStageId || row.wbsStageId;
    const currentStage = byId[currentStageId];

    if (isEditingRow) {
      return (
        <select
          value={currentStageId || ""}
          onChange={(e) => handleSelectChange(e, row.order)}
          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select Stage</option>
          {stages
            .filter((s) => s.isActive && s.clpEligible)
            .map((stage) => (
              <option key={stage.wbsStageId} value={stage.wbsStageId}>
                {stage.label}
              </option>
            ))}
        </select>
      );
    }

    return currentStage ? (
      <span className="block text-sm leading-tight" style={{ wordBreak: "break-word", whiteSpace: "normal", lineHeight: "1.2" }}>
        {currentStage.label}
      </span>
    ) : "N/A";
  };

  const renderTarget = (row) => {
    const isEditingRow = row.order === editingRowId;
    // v2: resolve stage from wbsStage embed or byId
    const currentStageId = row.wbsStage?.wbsStageId || row.wbsStageId;
    const stage = byId[currentStageId];

    // v2: gate by isSlab(stage) — NOT typeName === "SLAB"
    if (isSlab(stage)) {
      if (isEditingRow) {
        return (
          <input
            type="text"
            value={row.floorTarget || ""}
            onChange={(e) => handleInputChange(e, row.order, "floorTarget")}
            className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Floor target"
          />
        );
      }
      return <span className="text-sm">{row.floorTarget || "—"}</span>;
    }
    return "N/A";
  };

  return (
    <div className="h-full">
      {loading && (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
      {error && <p className="text-red-500 bg-red-50 p-4 rounded-lg border border-red-200">{error}</p>}

      {!loading && !error && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-12">#</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-40">Stage</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Particulars</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-56">Consideration</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-24">Floor Target</th>
                  {hasPrivilege("canEdit") && (
                    <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-36">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {projectStatus
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((row) => (
                    <tr key={row.clpLineId || row.order} className="hover:bg-slate-50 transition-colors duration-150">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-400 align-top">{row.order}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 align-top">{renderStatusType(row)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 align-top">
                        {row.order === editingRowId ? (
                          <textarea
                            value={row.particulars || ""}
                            onChange={(e) => handleInputChange(e, row.order, "particulars")}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 resize-none"
                            rows={2}
                            placeholder="Enter particulars..."
                          />
                        ) : (
                          <span className="text-sm">{row.particulars || <span className="text-slate-300">—</span>}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm align-top">
                        {row.order === editingRowId ? (
                          <input
                            type="number"
                            value={row.percentConsideration || 0}
                            onChange={(e) => handleInputChange(e, row.order, "percentConsideration")}
                            className="w-20 px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400"
                            min="0"
                            max="100"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-[60px]">
                              <div
                                className="h-full bg-gradient-to-r from-flamingo-400 to-flamingo-600 transition-all duration-300"
                                style={{ width: `${Math.min(100, Math.max(0, row.percentConsideration || 0))}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-slate-700 tabular-nums shrink-0">
                              {row.percentConsideration || 0}%
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 align-top">{renderTarget(row)}</td>
                      {hasPrivilege("canEdit") && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 align-top">
                          {row.order === editingRowId ? (
                            <div className="flex space-x-1">
                              <PrimaryButton
                                className="px-2 w-1/2 py-1 text-xs"
                                onClick={() => handleSaveRow(row)}
                                disabled={savingRowId === row.order}
                              >
                                {savingRowId === row.order ? (
                                  <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                  </div>
                                ) : (
                                  "Save"
                                )}
                              </PrimaryButton>
                              <OutlineButton
                                className="px-2 w-1/2 py-1 text-xs"
                                onClick={() => handleCancelNewRowEdit(row.order)}
                                disabled={savingRowId === row.order}
                              >
                                Cancel
                              </OutlineButton>
                            </div>
                          ) : (
                            <div className="flex space-x-1">
                              <EditButton onClick={() => handleEditRow(row.order)} />
                              <DeleteButton onClick={() => confirmDeleteRow(row)} />
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                {hasPrivilege("canEdit") && (
                  <tr>
                    <td colSpan={6} className="px-4 py-3 bg-slate-50/50 border-t border-slate-200">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        disabled={isAddRowDisabled}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-flamingo-600 hover:text-flamingo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus size={14} />
                        Add CLP Line
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onConfirm={handleDeleteRow}
        onCancel={handleCancelDelete}
        message="Are you sure you want to delete this CLP line?"
      />
    </div>
  );
}

export default ProjectStatus;
